import React, { useEffect } from 'react';
import { getCurrentUser } from '../lib/auth';
import Sidebar from '../components/Sidebar';
import { Tab } from '../lib/types';

// Complete calendar events data
const calendarEvents = [
  {
    title: "Semaine des métiers du numérique",
    date: "20-24",
    location: "France entière",
    interest: "Sourcing métiers IT, job dating, ateliers, rencontres candidats",
    month: 0
  },
  {
    title: "Salon du Travail et de la Mobilité Professionnelle",
    date: "16-17",
    location: "Paris",
    interest: "Recrutement tous secteurs, reconversion professionnelle",
    month: 0
  },
  {
    title: "Forum Emploi Tech",
    date: "8-9",
    location: "Lyon",
    interest: "Recrutement IT, développeurs, data scientists",
    month: 1
  },
  {
    title: "Semaine de l'industrie",
    date: "1-7",
    location: "France entière",
    interest: "Focus sur les métiers industriels, visites d'entreprises",
    month: 2
  },
  {
    title: "Paris Healthcare Week",
    date: "14-16",
    location: "Paris",
    interest: "Recrutement secteur santé, innovations RH santé",
    month: 3
  },
  {
    title: "Viva Technology",
    date: "15-18",
    location: "Paris",
    interest: "Innovation tech, startups, networking",
    month: 4
  },
  {
    title: "Salon RH",
    date: "4-5",
    location: "Paris",
    interest: "Solutions RH, conférences, networking",
    month: 5
  },
  {
    title: "Tech Job Fair",
    date: "12-13",
    location: "Bordeaux",
    interest: "Recrutement tech, startups, scale-ups",
    month: 6
  },
  {
    title: "Forum de l'alternance",
    date: "28-29",
    location: "France entière",
    interest: "Recrutement alternants, apprentissage",
    month: 7
  },
  {
    title: "Paris Retail Week",
    date: "10-12",
    location: "Paris",
    interest: "Innovation retail, recrutement commerce",
    month: 8
  },
  {
    title: "Salon Top Recrutement",
    date: "5",
    location: "Paris",
    interest: "Recrutement cadres et jeunes diplômés",
    month: 9
  },
  {
    title: "Semaine pour l'emploi des personnes handicapées",
    date: "13-19",
    location: "France entière",
    interest: "Inclusion, diversité, handicap",
    month: 10
  },
  {
    title: "Forum Emploi Senior",
    date: "5-6",
    location: "Lyon",
    interest: "Recrutement seniors, reconversion",
    month: 11
  }
];

const CalendarPage = () => {
  const [user] = React.useState(getCurrentUser());
  const [activeTab, setActiveTab] = React.useState<Tab>('calendar');

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
  };

  useEffect(() => {
    // Initialize calendar functionality after component mount
    const script = document.createElement('script');
    script.textContent = `
      function displayEvents(month = 'all') {
        const calendarContainer = document.getElementById('calendar');
        if (!calendarContainer) return;
        
        calendarContainer.innerHTML = '';
        
        let filteredEvents = ${JSON.stringify(calendarEvents)};
        if (month !== 'all') {
          filteredEvents = filteredEvents.filter(event => event.month === parseInt(month));
        }
        
        if (filteredEvents.length === 0) {
          const noEventsMsg = document.createElement('div');
          noEventsMsg.classList.add('no-events');
          noEventsMsg.innerHTML = '<h3>Aucun événement pour ce mois</h3><p>Veuillez sélectionner un autre mois ou afficher tous les événements.</p>';
          calendarContainer.appendChild(noEventsMsg);
          return;
        }
        
        // Sort events by date
        filteredEvents.sort((a, b) => {
          const monthDiff = a.month - b.month;
          if (monthDiff !== 0) return monthDiff;
          
          const dayA = parseInt(a.date.split("-")[0]);
          const dayB = parseInt(b.date.split("-")[0]);
          return dayA - dayB;
        });
        
        // Create event cards
        filteredEvents.forEach(event => {
          const eventCard = document.createElement('div');
          eventCard.classList.add('event-card');
          
          const monthNames = [
            "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
            "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
          ];
          
          eventCard.innerHTML = \`
            <div class="event-date">\${event.date} \${monthNames[event.month]}</div>
            <div class="event-title">\${event.title}</div>
            <div class="event-location">\${event.location}</div>
            <div class="event-interest">\${event.interest}</div>
          \`;
          
          calendarContainer.appendChild(eventCard);
        });
      }

      // Initialize calendar with all events
      displayEvents();
      
      // Handle month filters
      document.querySelectorAll('.month-btn').forEach(button => {
        button.addEventListener('click', function() {
          document.querySelectorAll('.month-btn').forEach(btn => btn.classList.remove('active'));
          this.classList.add('active');
          displayEvents(this.dataset.month);
        });
      });
    `;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <div className="flex min-h-screen">
      <Sidebar activeTab={activeTab} onTabChange={handleTabChange} user={user} />
      <div className="flex-1 p-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="header text-center mb-8">
            <h1 className="text-3xl font-bold text-[#ED285F] mb-4">54 dates clés pour les RH en 2025</h1>
            <p className="text-gray-600">Calendrier interactif des événements incontournables pour les recruteurs et RH</p>
          </div>
          
          <div className="month-filter flex flex-wrap justify-center gap-2 mb-8">
            <button className="month-btn active" data-month="all">Tous</button>
            <button className="month-btn" data-month="0">Janvier</button>
            <button className="month-btn" data-month="1">Février</button>
            <button className="month-btn" data-month="2">Mars</button>
            <button className="month-btn" data-month="3">Avril</button>
            <button className="month-btn" data-month="4">Mai</button>
            <button className="month-btn" data-month="5">Juin</button>
            <button className="month-btn" data-month="6">Juillet</button>
            <button className="month-btn" data-month="7">Août</button>
            <button className="month-btn" data-month="8">Septembre</button>
            <button className="month-btn" data-month="9">Octobre</button>
            <button className="month-btn" data-month="10">Novembre</button>
            <button className="month-btn" data-month="11">Décembre</button>
          </div>
          
          <div id="calendar" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"></div>
          
          <a href="https://pimpmyjob.fr/" className="block w-full max-w-lg mx-auto mt-12 px-8 py-4 text-center text-white font-bold rounded-full bg-[#ED285F] hover:bg-[#D01F4E] transition-colors shadow-lg">
            Créer des offres d'emploi irrésistibles avec pimp my job
          </a>
        </div>

        <style>{`
          .month-btn {
            background-color: white;
            border: 1px solid rgba(237, 40, 95, 0.3);
            border-radius: 20px;
            padding: 8px 16px;
            cursor: pointer;
            transition: all 0.2s;
            font-size: 14px;
          }
          
          .month-btn:hover {
            background-color: rgba(237, 40, 95, 0.1);
          }
          
          .month-btn.active {
            background-color: #ED285F;
            color: white;
          }
          
          .event-card {
            background-color: white;
            border-radius: 10px;
            padding: 20px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
            transition: transform 0.2s;
            border-left: 4px solid #ED285F;
          }
          
          .event-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
          }
          
          .event-date {
            font-size: 14px;
            color: #ED285F;
            font-weight: bold;
            margin-bottom: 8px;
          }
          
          .event-title {
            font-size: 18px;
            margin-bottom: 8px;
            font-weight: 600;
          }
          
          .event-location {
            font-size: 14px;
            color: #666;
            margin-bottom: 12px;
          }
          
          .event-interest {
            font-size: 14px;
            background-color: rgba(237, 40, 95, 0.1);
            padding: 8px;
            border-radius: 6px;
          }
          
          .no-events {
            grid-column: 1 / -1;
            text-align: center;
            padding: 40px;
            background-color: white;
            border-radius: 10px;
          }
        `}</style>
      </div>
    </div>
  );
};

export default CalendarPage;